<template>
  <div class="modal-card" style="width: auto">
    <header class="modal-card-head">
      <p class="modal-card-title is-centered">
        {{ title }}
      </p>
    </header>

    <section class="modal-card-body">
      <form method="post" @submit.prevent="onSubmitReview" novalidate>
        <form-wrapper :validator="$v.form">
          <div
            class="columns"
            style="height: calc(100vh - 170px); overflow: scroll"
          >
            <div
              class="column is-narrow"
              style="height: 100%; border-right: #1e7c9c dashed 3px;"
            >
              <form-group
                name="sales_code"
                attribute="attributes.gift_sales"
                :label="$t('attributes.gift_sales')"
                label-position="on-border"
              >
                <b-input
                  v-if="form.applications.length > 0"
                  v-model="form.sales_name"
                  name="sales_name"
                  disabled
                ></b-input>
                <b-autocomplete
                  v-else
                  ref="salesSelect"
                  v-model="employeeSearch"
                  name="sales_code"
                  field="name"
                  keep-first
                  clearable
                  select-on-click-outside
                  placeholder="搜尋業務編號/姓名"
                  :custom-formatter="e => `[${e.emp_code}] ${e.name}`"
                  :data="employees"
                  :loading="isLoadingEmployees"
                  :open-on-focus="false"
                  @typing="loadEmployees"
                  @select="onSelectEmployee"
                >
                  <template #default="props">
                    <p>
                      [{{ props.option.emp_code }}]
                      <span v-html="props.option.name"></span>
                    </p>
                  </template>
                  <template #empty>
                    {{
                      employeeSearch
                        ? isLoadingEmployees
                          ? $t('attributes.searching')
                          : $t('attributes.not_found')
                        : '搜尋業務編號/姓名'
                    }}
                  </template>
                </b-autocomplete>
              </form-group>

              <div>
                <p
                  class="is-size-5 has-text-weight-semibold has-text-centered has-text-warning-dark"
                >
                  {{ $t('attributes.gift_review_amount') }}:
                  {{ formatCurrency(totalAmount) }}
                </p>
              </div>

              <b-table
                :data="applications"
                :mobile-cards="false"
                hoverable
                narrowed
                sticky-header
                height="100%"
                :row-class="rowClass"
                style="width: 300px; height: calc(100vh - 270px);"
                @select="onSelectApplication"
              >
                <b-table-column
                  v-if="isFit"
                  field="user"
                  :label="`${$t('attributes.user')}/自購`"
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.user_name || '自購' }}
                </b-table-column>
                <b-table-column
                  v-if="isGroup"
                  field="user"
                  :label="$t('attributes.travel_group')"
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.group_code }}
                </b-table-column>
                <b-table-column
                  v-if="isRepurchase"
                  field="user"
                  label="公司買回"
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.remark }}
                </b-table-column>
                <b-table-column
                  field="amount"
                  :label="$t('attributes.gift_amount')"
                  numeric
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ formatCurrency(props.row.amount) }}
                </b-table-column>
                <b-table-column width="50" v-slot="props">
                  <b-button
                    type="is-danger"
                    size="is-small"
                    icon-left="delete"
                    rounded
                    :disabled="props.row.is_edit"
                    @click.prevent.stop="onDeleteApplication(props.row)"
                  ></b-button>
                </b-table-column>

                <template #footer v-if="form.applications.length > 0">
                  <div class="has-text-right">
                    {{ $t('attributes.gift_review_amount') }}:
                    {{ formatCurrency(totalAmount) }}
                  </div>
                </template>
              </b-table>
            </div>

            <div class="column" style="height: 100%">
              <section v-if="form.sales_code && !isRepurchase">
                <application-create-form
                  v-if="editingApplication"
                  :type="type"
                  :mode="editingMode"
                  :sales_code="form.sales_code"
                  :sales_department="form.sales_department"
                  :application="editingApplication"
                  :apply-gifts="applyGifts"
                  :sending="sending"
                  @send="saveApplication"
                  @cancel="onCancelApplication"
                />
              </section>
              <section v-if="isRepurchase">
                <application-repurchase-form
                  :sales_code="$store.getters.empCode"
                  @send="saveApplication"
                />
              </section>
              <section v-else class="section">
                <h1 class="title">請先選擇主辦業務</h1>
              </section>
            </div>
          </div>
        </form-wrapper>
      </form>
    </section>

    <footer class="modal-card-foot is-flex is-justify-content-left">
      <b-button
        :loading="sending"
        type="is-second"
        style="width: 140px"
        @click="onCancelReview"
        tabindex="20"
      >
        {{ $t('attributes.cancel') }}
      </b-button>
      <b-button
        :loading="sending"
        type="is-primary"
        style="width: 140px"
        @click="onSubmitReview"
        :disabled="
          this.editingMode === 'modify' || this.form.applications.length === 0
        "
        tabindex="21"
      >
        {{ $t('attributes.gift_submit') }}
      </b-button>
    </footer>

    <b-loading
      :active.sync="isLoading"
      :is-full-page="false"
      :can-cancel="false"
    ></b-loading>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { createGuid } from 'v-calendar/src/utils'
import { EMPLOYEES_API, GIFT_REVIEWS_API } from '@/utils/apis'
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import ApplicationCreateForm from '@/components/gifts/ApplicationCreateForm'
import ApplicationRepurchaseForm from '@/components/gifts/ApplicationRepurchaseForm.vue'
import { GIFT_REVIEW_APPLY_TYPES } from '@/utils/constants'

export default {
  name: 'ReviewCreateForm',
  components: { ApplicationCreateForm, ApplicationRepurchaseForm },

  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return [
          GIFT_REVIEW_APPLY_TYPES.FIT,
          GIFT_REVIEW_APPLY_TYPES.GROUP,
          GIFT_REVIEW_APPLY_TYPES.REPURCHASE
        ].includes(value)
      }
    },
    review: {
      type: Object,
      required: true
    }
  },
  data() {
    let title = this.$t('attributes.gift_application')
    switch (this.type) {
      case GIFT_REVIEW_APPLY_TYPES.FIT:
        title += ` - ${this.$t('attributes.gift_application_fit')}`
        break
      case GIFT_REVIEW_APPLY_TYPES.GROUP:
        title += ` - ${this.$t('attributes.gift_application_group')}`
        break
      case GIFT_REVIEW_APPLY_TYPES.REPURCHASE:
        title = `${this.$t('attributes.gift_application_repurchase')}`
    }
    return {
      title: title,
      isFit: this.type === GIFT_REVIEW_APPLY_TYPES.FIT,
      isGroup: this.type === GIFT_REVIEW_APPLY_TYPES.GROUP,
      isRepurchase: this.type === GIFT_REVIEW_APPLY_TYPES.REPURCHASE,

      isLoadingEmployees: false,
      employees: [],
      employeeSearch: null,

      sending: false,
      editingMode: null,
      editingApplication: null,
      applyGifts: [],

      form: this.extractForm(this.review)
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingEmployees || this.sending
    },
    applications() {
      return this.form.applications.filter(a => a.status !== 'delete')
    },
    canAdd() {
      if (!this.form.sales_code) {
        return false
      }

      return this.editingMode !== 'modify'
    },
    totalAmount() {
      return this.applications
        .filter(a => a.status !== 'delete')
        .reduce((sum, app) => sum + app.amount, 0)
    }
  },
  validations() {
    return {
      form: {
        sales_code: { required },
        applications: { required }
      }
    }
  },
  mounted() {
    if (this.form.applications.length === 0) {
      this.$refs.salesSelect.focus()
    }
    if (this.isRepurchase) {
      this.setEmployee()
    }
  },
  methods: {
    async setEmployee() {
      this.employeeSearch = this.$store.getters.empCode
      await this.loadEmployeesAsync()
      this.onSelectEmployee(this.employees[0])
    },
    extractForm(review) {
      return {
        sales_code: !this.isRepurchase
          ? review.sales_code
          : this.$store.getters.empCode,
        sales_name: review.sales_name,
        sales_department: review.sales_department,
        applications: review.applications
      }
    },
    resetApplication() {
      this.editingMode = 'new'
      this.editingApplication = {
        id: createGuid(),
        is_edit: true,
        status: 'new',
        is_public: false,
        user_id: null,
        user_identity: null,
        user_name: null,
        user_phone: null,
        group_code: null,
        group_name: null,
        group_departure_date: null,
        gifts: [],
        amount: 0,
        remark: null
      }
      this.applyGifts = this.collectApplyGifts()
    },

    async loadEmployeesAsync() {
      const api = `${EMPLOYEES_API}/search`
      const params = {
        search: this.employeeSearch
      }

      this.isLoadingEmployees = true
      try {
        const res = await this.axios.get(api, { params })
        this.employees = res.data.data
      } catch (e) {
        this.employees = []

        notifyService.fetch_error('employee', e)
      } finally {
        this.isLoadingEmployees = false
      }
    },
    loadEmployees: debounce(function() {
      if (!this.employeeSearch) {
        this.employees = []
        return
      }

      this.loadEmployeesAsync()
    }, 1000),
    onSelectEmployee(option) {
      this.form.sales_code = option && option.emp_code
      this.form.sales_department = option && option.depart_no
      this.form.sales_name = option && option.name
      this.resetApplication()
    },

    onSelectApplication(item) {
      if (this.editingMode) {
        this.editingApplication.is_edit = false
      }

      this.editingMode = 'modify'
      this.editingApplication = item
      this.editingApplication.is_edit = true
      this.applyGifts = this.collectApplyGifts()
    },
    onCancelApplication() {
      this.editingMode = null
      this.editingApplication.is_edit = false
      this.editingApplication = null

      this.resetApplication()
    },
    onDeleteApplication(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteApplication(item)
        }
      })
    },

    collectApplyGifts() {
      let applications = this.applications.filter(a => a.status !== 'delete')

      if (this.editingApplication) {
        applications = applications.filter(
          a => a.id !== this.editingApplication.id
        )
      }

      // 總共申請的贈品數量，除了目前所選擇的項目
      return applications.reduce((giftList, app) => {
        return app.gifts.reduce((items, gift) => {
          const index = items.findIndex(g => g.id === gift.id)
          if (index === -1) {
            items.push({ ...gift })
          } else {
            items[index].quantity += gift.quantity
          }

          return items
        }, giftList)
      }, [])
    },
    saveApplication(app) {
      if (this.editingMode === 'new') {
        if (this.type === GIFT_REVIEW_APPLY_TYPES.FIT) {
          if (app.user_id) {
            const index = this.form.applications.findIndex(
              a => a.user_id === app.user_id
            )
            if (index !== -1) {
              notifyService.error('此會員已經在申請列表中')
              return
            }
          } else {
            const index = this.form.applications.findIndex(a => !a.user_id)
            if (index !== -1) {
              notifyService.error('自購已在申請列表中')
              return
            }
          }
        } else if (this.type === GIFT_REVIEW_APPLY_TYPES.GROUP) {
          const index = this.form.applications.findIndex(
            a => a.group_code === app.group_code
          )
          if (index !== -1) {
            notifyService.error('此團已經在申請列表中')
            return
          }
        }
        app.status = 'add'
        app.is_edit = false
        this.form.applications = [...this.form.applications, app]

        this.resetApplication()
      } else {
        if (app.status === 'new') {
          app.status = 'add'
        } else if (app.status === '') {
          app.status = 'modify'
        }
        app.is_edit = false

        const idx = this.form.applications.findIndex(a => a.id === app.id)
        const applications = [...this.form.applications]
        applications[idx] = app
        this.form.applications = applications

        this.resetApplication()
      }
    },
    deleteApplication(app) {
      // if (app.status === 'new' || app.status === 'add') {
      //   this.form.applications = this.form.applications.filter(
      //     a => a.id !== app.id
      //   )
      // } else {
      //   app.status = 'delete'
      // }
      this.form.applications = this.form.applications.filter(
        a => a.id !== app.id
      )
      this.applyGifts = this.collectApplyGifts()
    },

    async onSubmitReview() {
      try {
        const data = {
          sales_code: this.form.sales_code,
          applications: this.form.applications.map(app => {
            return {
              id: app.id,
              status: app.status,
              is_public: app.is_public,
              user_id: app.user_id,
              group_code: app.group_code,
              group_name: app.group_name,
              group_departure_date: app.group_departure_date,
              gifts: app.gifts,
              remark: app.remark
            }
          })
        }
        let api = `${GIFT_REVIEWS_API}/${this.type}`

        this.sending = true
        const res = await this.axios.post(api, data)
        const review = res.data.data

        notifyService.success(this.$t('attributes.gift_submit_succeed'))

        this.$emit('saved', review)
      } catch (e) {
        notifyService.error(this.$t('attributes.gift_submit_failed'), e)
      } finally {
        this.sending = false
      }
    },
    onCancelReview() {
      const modifies = this.applications.filter(a => a.status !== '')

      if (modifies.length === 0) {
        this.$emit('cancel')
        return
      }

      this.$buefy.dialog.confirm({
        message: '尚未送審，確定放棄？',
        onConfirm: () => {
          this.$parent.close()
        }
      })
    },

    rowClass(row) {
      return row.is_edit ? 'has-background-light' : ''
    }
  }
}
</script>
